import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import DemandCalendar from "@/components/DemandCalendar";
import DayModal from "@/components/DayModal";
import BookingsTab from "@/components/BookingsTab";
import PaceTab from "@/components/PaceTab";
import DecisionCenter from "@/components/DecisionCenter";
import EventsPanel from "@/components/EventsPanel";
import GeoContextPanel from "@/components/GeoContextPanel";
import MarketPanel from "@/components/MarketPanel";
import MarketProgress from "@/components/MarketProgress";
import PriceAdvisor from "@/components/PriceAdvisor";
import PaceStrategy from "@/components/PaceStrategy";
import LearningEngine from "@/components/LearningEngine";import CompetitorIntelligence from "@/components/CompetitorIntelligence";
import ActionCenter from "@/components/ActionCenter";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Plus, Sparkles, MapPin, Plane, Train, Waves, Ship, RefreshCw, Gauge, Users, BedDouble, Euro, ShieldCheck, CalendarClock, BrainCircuit, Info, Pencil, Trash2, Loader2 } from "lucide-react";
import EditPropertyModal from "@/components/EditPropertyModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from "recharts";
import { toast } from "sonner";
import MetricDetailModal from "@/components/MetricDetailModal";

const priorityColor = { high: "var(--demand-high)", medium: "var(--demand-medium)", low: "var(--demand-low)" }; // eslint-disable-line no-unused-vars


function KPICard({ label, value, sub, secondary, period, icon: Icon, accent, testid, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} type="button"
      className="bg-card border border-border rounded-lg p-5 h-full w-full text-left hover:ring-2 hover:ring-primary/25 hover:-translate-y-0.5 transition-all cursor-pointer">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" style={accent ? { color: accent } : undefined} />
        <span className="text-[11px] uppercase tracking-widest font-bold">{label}</span>
        <Info className="w-3 h-3 ml-auto opacity-40" />
      </div>
      <p className="font-data text-3xl font-semibold leading-none" style={accent ? { color: accent } : undefined}>{value}</p>
      {secondary && <p className="text-sm font-data font-bold text-primary mt-1" data-testid={`${testid}-net`}>{secondary}</p>}
      {period && <p className="text-[10px] uppercase tracking-wide text-primary/70 font-bold mt-1.5">{period}</p>}
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </button>
  );
}

function Gauge2({ value, label, icon: Icon, onClick, period }) {
  return (
    <button type="button" onClick={onClick} className="bg-card border border-border rounded-lg p-6 h-full w-full text-left hover:ring-2 hover:ring-primary/25 transition-all cursor-pointer">
      <div className="flex items-center gap-2 text-muted-foreground mb-3">
        <Icon className="w-4 h-4" /><span className="text-xs uppercase tracking-widest font-bold">{label}</span>
        <Info className="w-3 h-3 ml-auto opacity-40" />
      </div>
      <p className="font-data text-4xl font-semibold">{value}<span className="text-lg text-muted-foreground">/100</span></p>
      {period && <p className="text-[10px] uppercase tracking-wide text-primary/70 font-bold mt-1.5">{period}</p>}
      <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: value >= 66 ? "var(--demand-high)" : value >= 40 ? "var(--demand-medium)" : "var(--demand-low)" }} />
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { t, lang, properties, activeId, selectProperty, loadProperties, deleteProperty, user } = useApp();
  const nav = useNavigate();
  const [prop, setProp] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingToday, setRefreshingToday] = useState(false);
  const [todayConfirmOpen, setTodayConfirmOpen] = useState(false);
  const [todayPriceInfo, setTodayPriceInfo] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forecast, setForecast] = useState([]);
  const [calItems, setCalItems] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [marketUpdatedAt, setMarketUpdatedAt] = useState(null);
  const [learningStats, setLearningStats] = useState(null);
  const [eventsActive, setEventsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState(null);
  const [decLoading, setDecLoading] = useState(true);
  const [selDay, setSelDay] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [horizon, setHorizon] = useState(7);
  const [tab, setTab] = useState("forecast");
  const [metric, setMetric] = useState(null);
  const [metricOpen, setMetricOpen] = useState(false);
  const [advisorDate, setAdvisorDate] = useState(new Date().toISOString().slice(0, 10));
  const advisorAutoRef = useRef(false);
  const decPollRef = useRef(null);

  const fetchDecisions = useCallback(async (id, silent = false, force = false) => {
    if (!silent) setDecLoading(true);
    try {
      const q = force ? `?lang=${lang}&refresh=true` : `?lang=${lang}`;
      const { data } = await api.get(`/properties/${id}/decision-center${q}`);
      setDecisions(data);
      if (decPollRef.current) { clearTimeout(decPollRef.current); decPollRef.current = null; }
      if (data?.freshness?.refreshing) {
        decPollRef.current = setTimeout(() => fetchDecisions(id, true), 30000);
      }
    } catch {} finally { if (!silent) setDecLoading(false); }
  }, [lang]);

  useEffect(() => () => { if (decPollRef.current) clearTimeout(decPollRef.current); }, []);

  // On first forecast load, if TODAY is fully booked, start the price advice on the
  // first upcoming day that still has a free room (user can still change the date).
  useEffect(() => {
    if (advisorAutoRef.current || !forecast.length) return;
    advisorAutoRef.current = true;
    const first = forecast.find((f) => !(f?.own_availability && f.own_availability.available === 0));
    if (first && first.date !== advisorDate) setAdvisorDate(first.date);
  }, [forecast]);

  const active = properties.find((p) => p.id === activeId) || properties[0];

  const loadAll = useCallback(async (id) => {
    setLoading(true);
    try {
      const [p, fc, comp, hist, learn] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/properties/${id}/forecast?horizon=90`),
        api.get(`/properties/${id}/competitors`),
        api.get(`/properties/${id}/price-changes`),
        api.get(`/properties/${id}/learning`),
      ]);
      setProp(p.data); setForecast(fc.data.forecast); setCompetitors(comp.data.competitors || []); setHistory(hist.data);
      setSummary(fc.data.summary); setEventsActive(fc.data.events_active); setLearningStats(learn.data);
      setMarketUpdatedAt(fc.data.market_updated_at || null);
      // Calendario in basso: usa la STESSA fonte del Price Advisor / Decision Center
      // (/price-calendar), così i prezzi mostrati coincidono con il consiglio prezzi.
      try {
        const { data: pc } = await api.get(`/properties/${id}/price-calendar?days=90&lang=${lang}`);
        const dmd = (v) => (v >= 66 ? "red" : v >= 40 ? "yellow" : "green");
        setCalItems((pc.calendar || []).map((r) => ({
          date: r.date, recommended_price: r.recommended_price,
          color: dmd(r.demand_index || 0), demand_index: r.demand_index,
          level: r.level, confidence: r.confidence, own_availability: r.own_availability,
        })));
      } catch { setCalItems([]); }
    } catch (e) { toast.error("Load error"); }
    setLoading(false);
    fetchDecisions(id);
  }, [lang, fetchDecisions]);

  useEffect(() => { if (active) loadAll(active.id); }, [active, loadAll]);

  if (!properties.length) {
    return (
      <div className="App min-h-screen"><Nav />
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
          <Sparkles className="w-10 h-10 text-primary mx-auto mb-5" />
          <h1 className="text-3xl font-bold mb-3">{t("noProps")}</h1>
          <p className="text-muted-foreground mb-8">{t("createFirst")}</p>
          <Button data-testid="empty-add-btn" size="lg" onClick={() => nav("/properties/new")}><Plus className="w-4 h-4 mr-2" />{t("addProperty")}</Button>
        </div>
      </div>
    );
  }

  const openDay = (d) => { setSelDay(d); setModalOpen(true); };
  const chartData = calItems.slice(0, 30).map((d) => ({
    date: d.date.slice(5), demand: d.demand_index, price: d.recommended_price,
  }));
  const revData = forecast.map((d) => ({ date: d.date.slice(5), revenue: d.projected_revenue, occupancy: d.occupancy_pct }));
  const avgDemand = summary ? summary.avg_demand : 0;
  const compIndex = summary ? summary.competition_index : (forecast[0]?.competition_index || 0);
  const todayFc = forecast[0];
  // Price advice skips fully-booked days: use the first upcoming day that still has
  // at least one room free (falls back to today when availability is unknown).
  const recDay = forecast.find((f) => !(f?.own_availability && f.own_availability.available === 0)) || todayFc;
  const recDayIsToday = recDay && todayFc && recDay.date === todayFc.date;
  const fmtRecDay = recDay ? new Date(recDay.date).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short", day: "numeric", month: "short" }) : "";
  const isAdmin = user?.role === "admin";
  const marketFresh = (() => {
    if (!isAdmin) return null;
    if (!marketUpdatedAt) return lang === "it" ? "mercato mai aggiornato" : "market never updated";
    const diffH = (Date.now() - new Date(marketUpdatedAt).getTime()) / 36e5;
    if (isNaN(diffH)) return null;
    if (diffH < 1) return lang === "it" ? "dati mercato <1h fa" : "market data <1h ago";
    if (diffH < 24) return lang === "it" ? `dati mercato ${Math.round(diffH)}h fa` : `market data ${Math.round(diffH)}h ago`;
    return lang === "it" ? `dati mercato ${Math.round(diffH / 24)}g fa` : `market data ${Math.round(diffH / 24)}d ago`;
  })();
  const eur = (n) => new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-US").format(n || 0);
  const market = prop?.market || {};

  const refreshMarket = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await api.post(`/properties/${active.id}/refresh-market`);
      await loadAll(active.id);
      toast.success(lang === "it" ? "Mercato aggiornato" : "Market updated");
    } catch (e) {
      toast.error(lang === "it" ? "Errore nell'aggiornamento" : "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const refreshTodayPrice = async () => {
    setTodayConfirmOpen(false);
    if (refreshingToday) return;
    setRefreshingToday(true);
    try {
      const pid = active.id;
      const { data } = await api.post(`/properties/${pid}/market/refresh-today`);
      let st = {};
      // Lettura in background: polling ogni 5s (max ~3 min).
      for (let i = 0; data.self_pending && i < 36; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        st = (await api.get(`/properties/${pid}/market/today-status`)).data || {};
            if (st.status !== "running") break;
      }
      if (st.status === "running" || st.status === "error") {
        toast.error(lang === "it" ? "Lettura del prezzo di oggi non completata, riprova tra poco" : "Today's price read did not complete, try again shortly");
      } else {
        toast.success(
          lang === "it"
            ? `Prezzo di oggi aggiornato${st.price ? ` (€${st.price})` : ""} · ~${data.est_runs} chiamate Apify (~$${data.est_cost_usd})`
            : `Today's price refreshed${st.price ? ` (€${st.price})` : ""} · ~${data.est_runs} Apify calls (~$${data.est_cost_usd})`,
          { description: lang === "it" ? "Il Decision Center è stato aggiornato." : "The Decision Center has been updated." }
        );
      }
      setTodayPriceInfo({ price: st.price, asof: st.self_price_asof });
      await loadAll(pid);
      fetchDecisions(pid, false, true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "it" ? "Errore aggiornamento prezzo di oggi" : "Today price refresh failed"));
    } finally {
      setRefreshingToday(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  };

  const horizons = [["today", 1], ["next7", 7], ["next30", 30], ["next90", 90]];

  const it = lang === "it";
  const bfIncluded = !!(prop?.default_meal_plan && prop.default_meal_plan !== "room_only");
  const rateBasisLabel = it ? (bfIncluded ? "colazione inclusa" : "camera, senza colazione")
                             : (bfIncluded ? "breakfast included" : "room only, no breakfast");
  // Coerente con l'indice concorrenza (backend = len(market.competitors)). Usa `||`
  // per NON fermarsi su un competitor_count salvato a 0 (stale) quando la lista esiste.
  const compCount = (Array.isArray(market.competitors) ? market.competitors.length : 0)
    || competitors.length || market.competitor_count || 0;
  const histActive = summary?.history_active;
  const buildMetric = (key) => {
    const base = {
      periodLabel: t("periodLabel"), howLabel: t("howCalcLabel"), sourcesLabel: t("sourcesLabel"),
    };
    if (key === "price") {
      const reasonBits = [];
      reasonBits.push(it ? `domanda ${todayFc.demand_index}/100` : `demand ${todayFc.demand_index}/100`);
      reasonBits.push(it ? `occupazione prevista ${todayFc.occupancy_pct}%` : `expected occupancy ${todayFc.occupancy_pct}%`);
      reasonBits.push(compCount ? (it ? `${compCount} competitor` : `${compCount} competitors`) : (it ? "nessun competitor disponibile" : "no competitor data"));
      reasonBits.push(histActive ? (it ? "storico reale usato" : "real history used") : (it ? "nessuno storico" : "no history"));
      reasonBits.push((todayFc.events && todayFc.events.length) ? (it ? `${todayFc.events.length} eventi` : `${todayFc.events.length} events`) : (it ? "nessun evento" : "no events"));
      return { ...base, title: t("recommendedPrice"), value: `€${todayFc.recommended_price}`, period: t("today"),
        rows: [
          { label: t("currentPrice"), value: `€${todayFc?.current_price ?? prop?.base_price}` },
          { label: t("recommended"), value: `€${todayFc.recommended_price}` },
          { label: `${t("minPrice")} / ${t("maxPrice")}`, value: `€${todayFc.min_price} – €${todayFc.max_price}` },
          { label: it ? "Base tariffa" : "Rate basis", value: it ? (bfIncluded ? "Camera con colazione inclusa" : "Camera, senza colazione") : (bfIncluded ? "Room with breakfast included" : "Room only, no breakfast") },
        ],
        reason: reasonBits.join(", ") + ".",
        how: (it ? "Prezzo base × moltiplicatore di domanda (0,75–1,6) × fattore appreso dalle tue scelte. Quando esiste uno storico prenotazioni reale, l'ADR storico ha la priorità e viene fuso nel prezzo."
               : "Base price × demand multiplier (0.75–1.6) × factor learned from your choices. When real booking history exists, historical ADR takes priority and is blended in.")
             + (it ? (bfIncluded ? " Il prezzo consigliato è una tariffa CON colazione inclusa (impostazione struttura: B&B)."
                                 : " Il prezzo consigliato è una tariffa camera SENZA colazione (colazione/servizi extra esclusi).")
                   : (bfIncluded ? " The recommended price is a rate WITH breakfast included (property setting: B&B)."
                                 : " The recommended price is a room-only rate WITHOUT breakfast (breakfast/extras excluded).")),
        sources: [it ? "Domanda prevista" : "Demand", it ? "Storico prenotazioni" : "Booking history", "Competitor", it ? "Eventi" : "Events", "Meteo"] };
    }
    if (key === "occupancy") {
      return { ...base, title: t("avgOccupancy"), value: `${summary?.avg_occupancy}%`, period: it ? "Media prossimi 90 giorni" : "Avg next 90 days",
        rows: [{ label: t("today"), value: `${todayFc.occupancy_pct}%` }],
        how: it ? "Stimata dalla domanda prevista (0–100 → 30–100%). Con storico prenotazioni reale, l'occupazione realizzata storica ha la priorità."
               : "Estimated from forecast demand (0–100 → 30–100%). With real booking history, realized historical occupancy takes priority.",
        sources: [it ? "Domanda" : "Demand", it ? "Storico prenotazioni" : "Booking history"] };
    }
    if (key === "revenue") {
      return { ...base, title: t("revenue90"), value: `€${eur(summary?.total_revenue)}`, period: it ? "Prossimi 90 giorni" : "Next 90 days",
        how: it ? "Somma, per ogni giorno, di: camere × occupazione prevista × prezzo consigliato."
               : "Sum over each day of: rooms × expected occupancy × recommended price.",
        sources: [it ? "Prezzo consigliato" : "Recommended price", it ? "Occupazione prevista" : "Expected occupancy", it ? "N° camere" : "Rooms"] };
    }
    if (key === "demand") {
      return { ...base, title: t("demandIndex"), value: `${avgDemand}/100`, period: it ? "Media prossimi 30 giorni" : "Avg next 30 days",
        how: it ? "Punteggio 0–100 che unisce stagionalità, giorno della settimana, festività, meteo, eventi e (con priorità) l'occupazione storica reale."
               : "0–100 score combining seasonality, day of week, holidays, weather, events and (with priority) real historical occupancy.",
        sources: [it ? "Stagionalità" : "Seasonality", it ? "Festività" : "Holidays", "Meteo", it ? "Eventi" : "Events", it ? "Storico" : "History"] };
    }
    if (key === "competition") {
      return { ...base, title: t("competitionIndex"), value: `${compIndex}/100`, period: t("today"),
        rows: [{ label: it ? "Competitor rilevati" : "Competitors detected", value: compCount }],
        how: it ? "Densità dei competitor nel raggio configurato attorno alla struttura (più competitor = più concorrenza)."
               : "Density of competitors within the configured radius around your property (more competitors = more competition).",
        sources: ["OpenStreetMap", it ? "Analisi mercato" : "Market analysis"] };
    }
    if (key === "confidence") {
      const c = summary?.avg_confidence ?? 0;
      return { ...base, title: t("confidence"), value: `${Math.round(c * 100)}%`, period: it ? "Media prossimi 90 giorni" : "Avg next 90 days",
        how: it ? "Dipende dalla QUALITÀ e QUANTITÀ dei dati: storico prenotazioni, PMS collegato, competitor, eventi, meteo e vicinanza della data. Apri un giorno e tocca la percentuale per il dettaglio completo."
               : "Depends on data QUALITY and QUANTITY: booking history, connected PMS, competitors, events, weather and how near the date is. Open a day and tap the percentage for the full breakdown.",
        sources: [it ? "Storico" : "History", "PMS", "Competitor", it ? "Eventi" : "Events", "Meteo"] };
    }
    return null;
  };
  const openMetric = (key) => { setMetric(buildMetric(key)); setMetricOpen(true); };

  const setOutcome = async (id, outcome) => {
    try {
      const { data } = await api.patch(`/price-changes/${id}/outcome`, { outcome });
      setHistory((prev) => prev.map((h) => (h.id === id ? { ...h, outcome } : h)));
      if (data.learning) setLearningStats(data.learning);
      toast.success("OK");
    } catch { toast.error("Error"); }
  };

  return (
    <div className="App min-h-screen"><Nav />
      <DayModal propId={active.id} day={selDay} open={modalOpen} onClose={setModalOpen} onLogged={() => loadAll(active.id)} />
      <MetricDetailModal info={metric} open={metricOpen} onClose={setMetricOpen} />
      <EditPropertyModal prop={prop} open={editOpen} onClose={() => setEditOpen(false)}
        onSaved={async () => { await loadProperties(); await loadAll(active.id); }} />
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-property-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{lang === "it" ? "Eliminare questa struttura?" : "Delete this property?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "it"
                ? <>Stai per eliminare <strong>{active?.name}</strong>. Questa azione è irreversibile e i dati associati verranno rimossi.</>
                : <>You are about to delete <strong>{active?.name}</strong>. This action is irreversible and its associated data will be removed.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-property-cancel">{lang === "it" ? "Annulla" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-property-confirm" disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                setDeleting(true);
                try {
                  await deleteProperty(active.id);
                  toast.success(lang === "it" ? "Struttura eliminata" : "Property deleted");
                  setDeleteOpen(false);
                } catch {
                  toast.error(lang === "it" ? "Errore durante l'eliminazione" : "Error while deleting");
                } finally { setDeleting(false); }
              }}>
              {deleting ? (lang === "it" ? "Eliminazione..." : "Deleting...") : (lang === "it" ? "Elimina" : "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Select value={active.id} onValueChange={selectProperty}>
              <SelectTrigger data-testid="property-switcher" className="w-64 h-11 text-base font-semibold"><SelectValue /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="edit-property-btn" variant="ghost" size="sm" className="h-11 px-2.5" onClick={() => setEditOpen(true)} title={t("editTitle")}>
              <Pencil className="w-4 h-4 mr-1.5" />{t("editProperty")}
            </Button>
            {properties.length > 1 && (
              <Button data-testid="delete-property-btn" variant="ghost" size="sm"
                className="h-11 px-2.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setDeleteOpen(true)}
                title={lang === "it" ? "Elimina struttura" : "Delete property"}>
                <Trash2 className="w-4 h-4 mr-1.5" />{lang === "it" ? "Elimina" : "Delete"}
              </Button>
            )}
            {market.city && <span className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-4 h-4" />{market.city}</span>}
          </div>
          <div className="flex gap-2">
            <Button data-testid="refresh-market-btn" variant="outline" size="sm" onClick={refreshMarket} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}{t("refresh")}
            </Button>
            <Button data-testid="refresh-today-btn" variant="outline" size="sm" onClick={() => setTodayConfirmOpen(true)} disabled={refreshingToday}
              title={lang === "it" ? "Legge su Booking i prezzi dei competitor e il tuo prezzo per OGGI (usa Apify) e aggiorna il Decision Center" : "Reads competitor prices and your own price for TODAY from Booking (uses Apify) and updates the Decision Center"}>
              {refreshingToday ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}{lang === "it" ? "Aggiorna prezzo per oggi" : "Refresh today's price"}
            </Button>
            <Button data-testid="add-property-btn" size="sm" onClick={() => nav("/properties/new")}><Plus className="w-4 h-4 mr-2" />{t("addProperty")}</Button>
          </div>
        </div>
        <MarketProgress running={refreshing || refreshingToday} lang={lang} />
        {(() => {
          const asof = todayPriceInfo?.asof || todayFc?.current_price_asof;
          return asof ? (
            <p className="text-[11px] text-muted-foreground -mt-2 mb-4" data-testid="today-price-asof">
              {lang === "it" ? `Prezzo di oggi aggiornato il ${fmtDateTime(asof)}` : `Today's price updated on ${fmtDateTime(asof)}`}
            </p>
          ) : null;
        })()}

        <AlertDialog open={todayConfirmOpen} onOpenChange={setTodayConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{lang === "it" ? "Aggiornare il prezzo di oggi?" : "Refresh today's price?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {lang === "it"
                  ? "Verranno letti su Booking i prezzi dei competitor e soprattutto il TUO prezzo per oggi (circa 2 chiamate Apify). Il Decision Center verrà aggiornato con i dati di oggi."
                  : "This reads competitor prices and above all YOUR price for today from Booking (about 2 Apify calls). The Decision Center will be updated with today's data."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="refresh-today-cancel">{lang === "it" ? "Annulla" : "Cancel"}</AlertDialogCancel>
              <AlertDialogAction data-testid="refresh-today-confirm" onClick={refreshTodayPrice}>{lang === "it" ? "Procedi" : "Proceed"}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {decisions?.today_availability?.total ? (
          <div data-testid="today-occupancy-badge" className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3">
            <span className="inline-flex items-center gap-2">
              <BedDouble className="w-5 h-5 text-primary" />
              <span className="text-sm font-bold uppercase tracking-wide text-primary">{lang === "it" ? "Oggi" : "Today"}</span>
            </span>
            <span className="font-data text-lg font-semibold" data-testid="today-occupancy-value">
              {decisions.today_availability.occupied}/{decisions.today_availability.total} {lang === "it" ? "camere occupate" : "rooms occupied"}
            </span>
            <span className="text-sm text-muted-foreground">
              · {decisions.today_availability.available} {lang === "it" ? (decisions.today_availability.available === 1 ? "libera" : "libere") : (decisions.today_availability.available === 1 ? "free room" : "free rooms")}
            </span>
            <span className="ml-auto text-[11px] text-muted-foreground">
              {lang === "it" ? "dalle prenotazioni importate" : "from imported bookings"}
            </span>
            {Array.isArray(decisions.today_availability.occupied_rooms) && decisions.today_availability.occupied_rooms.length > 0 && (
              <details data-testid="today-occupancy-rooms" className="w-full mt-1">
                <summary className="cursor-pointer text-xs text-primary/80 hover:text-primary select-none">
                  {lang === "it" ? "Quali camere sono occupate oggi?" : "Which rooms are occupied today?"}
                </summary>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {decisions.today_availability.occupied_rooms.map((nm, i) => (
                    <li key={nm + i} data-testid={`occupied-room-${i}`} className="rounded-full bg-background border border-border px-2.5 py-0.5 text-xs text-muted-foreground">{nm}</li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
                  {lang === "it"
                    ? "Se vedi una camera che non è di questa struttura (o un doppione 'virtuale/guest'), eliminala dalla pagina Camere per escluderla dal conteggio."
                    : "If a room here isn't part of this property (or a 'virtual/guest' duplicate), delete it from the Rooms page to exclude it from the count."}
                </p>
              </details>
            )}
          </div>
        ) : null}

        {loading ? <p className="text-muted-foreground py-20 text-center">{t("loading")}</p> : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <DecisionCenter data={decisions} loading={decLoading} today={recDay} recDayIsToday={recDayIsToday} recDayLabel={fmtRecDay} lang={lang} onRefresh={() => active && fetchDecisions(active.id, false, true)} />
            {/* KPI strip */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <KPICard testid="kpi-price" onClick={() => openMetric("price")} period={recDayIsToday ? t("today") : (lang === "it" ? `Prossimo disp.: ${fmtRecDay}` : `Next avail.: ${fmtRecDay}`)}
                label={recDay?.city_tax_night > 0 ? (lang === "it" ? "Prezzo consigliato (Netto tassa escl.)" : "Recommended price (Net, tax excl.)") : (lang === "it" ? "Prezzo consigliato (Lordo)" : "Recommended price (Gross)")}
                value={`€${recDay?.recommended_price ?? "—"}`}
                secondary={recDay?.city_tax_night > 0
                  ? (recDay?.recommended_price_gross != null ? `${lang === "it" ? "Lordo Booking" : "Booking gross"} €${recDay.recommended_price_gross}` : null)
                  : (recDay?.recommended_price_net != null ? `${lang === "it" ? "Netto" : "Net"} €${recDay.recommended_price_net}` : null)}
                sub={recDay ? `${!recDayIsToday ? (lang === "it" ? "Oggi tutto occupato → " : "Today sold out → ") : ""}${rateBasisLabel} · ${t("minPrice")} €${recDay.min_price} · ${t("maxPrice")} €${recDay.max_price}${marketFresh ? ` · ${marketFresh}` : ""}` : ""} icon={Euro} accent="var(--primary)" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}>
              <KPICard testid="kpi-occupancy" onClick={() => openMetric("occupancy")} period={lang === "it" ? "Media 90 gg" : "Avg 90 days"} label={t("avgOccupancy")} value={`${summary?.avg_occupancy ?? 0}%`}
                sub={`${t("today")}: ${todayFc?.occupancy_pct ?? 0}%`} icon={BedDouble} />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
              <KPICard testid="kpi-revenue" onClick={() => openMetric("revenue")} period={lang === "it" ? "Prossimi 90 gg" : "Next 90 days"} label={t("revenue90")} value={`€${eur(summary?.total_revenue)}`}
                sub={`${forecast.length} ${lang === "it" ? "giorni" : "days"}`} icon={CalendarClock} />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
              <KPICard testid="kpi-confidence" onClick={() => openMetric("confidence")} period={lang === "it" ? "Media 90 gg" : "Avg 90 days"} label={t("confidence")} value={`${Math.round((summary?.avg_confidence ?? 0) * 100)}%`}
                sub={eventsActive ? t("events") : t("noEvents")} icon={ShieldCheck}
                accent={(summary?.avg_confidence ?? 0) >= 0.7 ? "var(--demand-low)" : (summary?.avg_confidence ?? 0) >= 0.5 ? "var(--demand-medium)" : "var(--demand-high)"} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-1"><Gauge2 value={avgDemand} onClick={() => openMetric("demand")} period={lang === "it" ? "Media 30 gg" : "Avg 30 days"} label={t("demandIndex")} icon={Gauge} /></motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="md:col-span-1"><Gauge2 value={compIndex} onClick={() => openMetric("competition")} period={t("today")} label={t("competitionIndex")} icon={Users} /></motion.div>

            {/* Cosa fare oggi — action center (Step 3) */}
            <ActionCenter propId={active.id} lang={lang} onPickDate={setAdvisorDate} />

            {/* Strategia Ritmo Vendite (pace: anticipo vs ritardo per mese) */}
            <PaceStrategy propId={active.id} lang={lang} onPickDate={setAdvisorDate} />

            {/* Learning Engine — aggressività personalizzata per struttura */}
            <LearningEngine propId={active.id} lang={lang} />

            {/* Price Advisor + Smart Calendar */}
            <PriceAdvisor propId={active.id} lang={lang} date={advisorDate} onDateChange={setAdvisorDate} />

            {/* Competitor Intelligence */}
            <CompetitorIntelligence propId={active.id} lang={lang} date={advisorDate} />

            {/* trend chart */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-4 bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-4">{t("demand")} & {t("price")} · 30gg</h3>
              <ResponsiveContainer width="100%" height={160}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Area yAxisId="l" type="monotone" dataKey="demand" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.15} />
                  <Line yAxisId="r" type="monotone" dataKey="price" stroke="hsl(var(--chart-3))" dot={false} strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Property geographic context */}
            <GeoContextPanel propId={active.id} prop={prop} />

            {/* Relevant events */}
            <EventsPanel propId={active.id} />

            {/* calendar — usa gli stessi dati del Price Advisor / Decision Center */}
            <div className="md:col-span-4"><DemandCalendar forecast={calItems} onSelect={openDay} /></div>
          </div>
        )}
      </div>
    </div>
  );
}
